import React, { useEffect, useState, useRef } from "react";
import Sidebar from "../../layout/Sidebar";
import Header from "../../layout/Header";
import Footer from "../../layout/Footer";
import { MODULE_PERMISSION_LIST } from "../../utils/modulePermissionList";
import PermissionSwitch from "./PermissionSwitch";

export default function ViewPermission() {
  const saveTimeout = useRef(null);

  const token = localStorage.getItem("adminToken");
  const API_URL = process.env.REACT_APP_BASE_URL_API || "";

  const [isEdit, setIsEdit] = useState(null);
  const [subadmin, setSubadmin] = useState({});
  const [permissions, setPermissions] = useState({});

  /* ================= GET USER ID ================= */
  useEffect(() => {
    const id = window.location.pathname.split("/").pop();
    setIsEdit(id);
  }, []);

  /* ================= FETCH SUB ADMIN ================= */
  useEffect(() => {
    if (!isEdit) return;

    fetch(`${API_URL}/api/admin/sub-admin-single/${isEdit}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((res) => res?.data && setSubadmin(res.data));
  }, [isEdit]);

  /* ================= FETCH PERMISSIONS ================= */
  useEffect(() => {
    if (!isEdit) return;
    fetchPermissions();
  }, [isEdit]);

  const fetchPermissions = async () => {
    try {
      const res = await fetch(
        `${API_URL}/api/admin/get-permissions/${isEdit}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const data = await res.json();

      if (data.success && data.data?.permissions?.length) {
        const mapped = {};
        data.data.permissions.forEach((p) => {
          mapped[p.module] = p.actions;
        });
        setPermissions(mapped);
      } else {
        initDefaultPermissions();
      }
    } catch {
      initDefaultPermissions();
    }
  };

  /* ================= DEFAULT PERMISSIONS ================= */
  const initDefaultPermissions = () => {
    const defaults = {};
    MODULE_PERMISSION_LIST.forEach((module) => {
      module.children.forEach((child) => {
        defaults[child.page] = {
          view: false,
          create: false,
          edit: false,
          delete: false,
        };
      });
    });
    setPermissions(defaults);
  };

  /* ================= HELPERS ================= */
  const getParent = (module) => module.children[0];
  const getChildren = (module) => module.children.slice(1);

  const isParentChecked = (module) => {
    const parent = getParent(module);
    return Object.values(permissions[parent.page] || {}).some(Boolean);
  };

  /* ================= TOGGLE PARENT ================= */
  const toggleParent = (module, checked) => {
    setPermissions((prev) => {
      const updated = { ...prev };

      module.children.forEach((child) => {
        updated[child.page] = {
          view: checked && child.actions.includes("view"),
          create: checked && child.actions.includes("create"),
          edit: checked && child.actions.includes("edit"),
          delete: checked && child.actions.includes("delete"),
        };
      });

      triggerAutoSave(updated);
      return updated;
    });
  };

  /* ================= TOGGLE CHILD ================= */
  const toggleChild = (page, action, module) => {
    setPermissions((prev) => {
      const updated = {
        ...prev,
        [page]: {
          ...prev[page],
          [action]: !prev[page]?.[action],
        },
      };

      const parent = getParent(module);
      const children = getChildren(module);

      const anyChildOn = children.some((c) =>
        Object.values(updated[c.page] || {}).some(Boolean)
      );

      updated[parent.page] = {
        view: anyChildOn,
        create: anyChildOn && parent.actions.includes("create"),
        edit: anyChildOn && parent.actions.includes("edit"),
        delete: anyChildOn && parent.actions.includes("delete"),
      };

      triggerAutoSave(updated);
      return updated;
    });
  };

  /* ================= AUTO SAVE ================= */
  const triggerAutoSave = (data) => {
    if (saveTimeout.current) clearTimeout(saveTimeout.current);
    saveTimeout.current = setTimeout(() => savePermissions(data), 500);
  };

  const savePermissions = async (permissionState) => {
    const payload = {
      user_id: isEdit,
      permissions: Object.keys(permissionState).map((module) => ({
        module,
        actions: permissionState[module],
      })),
    };

    await fetch(`${API_URL}/api/admin/upsert-permissions`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });
  };

  /* ================= UI ================= */
  return (
    <div className="container body">
      <div className="main_container">
        <Sidebar />
        <Header pageTitle="View Permission" />

        <div className="right_col" role="main">
         

          <div className="x_panel">
            <div className="x_title">
              <h2 className="text-danger">Name : {subadmin?.name} ({subadmin?.email})</h2>
              <div className="clearfix" />
            </div>
            <div className="x_content">
            {MODULE_PERMISSION_LIST.map((module) => {
              const parent = getParent(module);

              return (
                <div key={module.key} className="mb-4">
                  <h4 className="d-flex justify-content-between align-items-center">
                    {module.label}
                    <PermissionSwitch
                      checked={isParentChecked(module)}
                      onChange={(e) =>
                        toggleParent(module, e.target.checked)
                      }
                    />
                  </h4>

                  <table className="table table-bordered">
                    <thead>
                      <tr>
                        <th>Page</th>
                        <th>View</th>
                        <th>Create</th>
                        <th>Edit</th>
                        <th>Delete</th>
                      </tr>
                    </thead>

                    <tbody>
                      {getChildren(module).map((child) => (
                        <tr key={child.key}>
                          <td>{child.label}</td>

                          {["view", "create", "edit", "delete"].map(
                            (action) => (
                              <td className="text-center" key={action}>
                                {child.actions.includes(action) ? (
                                  <PermissionSwitch
                                    checked={
                                      permissions[child.page]?.[action] ??
                                      false
                                    }
                                    onChange={() =>
                                      toggleChild(
                                        child.page,
                                        action,
                                        module
                                      )
                                    }
                                  />
                                ) : (
                                  "—"
                                )}
                              </td>
                            )
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })}
          </div>
          </div>
          </div>

        <Footer />
      </div>
    </div>
  );
}
